import React, { useEffect, useState } from "react";
import {
  Button,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getApi, postApi } from "../../Utlis/apiService";
import {
  CREATE_COUPON,
  GET_ALL_COUPONS,
  GET_ELIGIBLE_USERS,
  GET_AVAILABLE_COUPONS,
  GET_COUPON_ASSIGNMENTS,
} from "../../Utlis/apiPath";
import { errorToast, successToast } from "../../Services/ToastHelper";
import Loader from "../../Components/Loader/Loader";
import "./Coupons.scss";

const { Title, Text } = Typography;
const { Option } = Select;

const CHIP_STYLES = {
  violet: { background: "#f5f3ff", color: "#6d28d9" },
  slate: { background: "#f1f5f9", color: "#475569" },
  amber: { background: "#fffbeb", color: "#b45309" },
  blue: { background: "#eff6ff", color: "#1d4ed8" },
  emerald: { background: "#ecfdf5", color: "#047857" },
  rose: { background: "#fff1f2", color: "#be123c" },
};

const StatChip = ({ tone, children }) => (
  <span
    style={{
      ...CHIP_STYLES[tone],
      display: "inline-block",
      minWidth: 32,
      textAlign: "center",
      fontWeight: 600,
      fontSize: 12.5,
      lineHeight: "20px",
      padding: "2px 10px",
      borderRadius: 6,
    }}
  >
    {children}
  </span>
);

const Coupons = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterStatus, setFilterStatus] = useState(undefined);
  const [filterActive, setFilterActive] = useState(undefined);
  const [filterUserId, setFilterUserId] = useState(undefined);
  const [filterUsersLoading, setFilterUsersLoading] = useState(false);
  const [filterEligibleUsers, setFilterEligibleUsers] = useState([]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);

  const [expiredCounts, setExpiredCounts] = useState({});
  const [expiredCountsLoading, setExpiredCountsLoading] = useState(false);

  const sortByRecentlyAdded = (list) =>
    [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // The list API has no expiry rollup of its own, so derive it by fetching
  // each visible coupon's assignments and counting the expired serials.
  const fetchExpiredCounts = async (couponList) => {
    if (!couponList || couponList.length === 0) {
      setExpiredCounts({});
      return;
    }
    setExpiredCountsLoading(true);
    try {
      const results = await Promise.allSettled(
        couponList.map((coupon) => getApi(`${GET_COUPON_ASSIGNMENTS}/${coupon.id}`))
      );
      const now = Date.now();
      const counts = {};
      results.forEach((result, index) => {
        const couponId = couponList[index].id;
        const assignments =
          result.status === "fulfilled" && result.value?.status === 200 && Array.isArray(result.value.data)
            ? result.value.data
            : [];
        // The backend's assignment `status` field doesn't flip to EXPIRED once the
        // expiry date passes, so derive it ourselves from expiryDate vs. now
        // (excluding serials that were already redeemed before expiring).
        counts[couponId] = assignments.filter(
          (assignment) =>
            assignment.expiryDate &&
            new Date(assignment.expiryDate).getTime() < now &&
            !(assignment.currentUsageCount > 0)
        ).length;
      });
      setExpiredCounts(counts);
    } finally {
      setExpiredCountsLoading(false);
    }
  };

  const fetchCoupons = async (status = filterStatus, active = filterActive) => {
    setIsLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (active !== undefined) params.active = active;

      const response = await getApi(GET_ALL_COUPONS, { params });
      if (response && response.status === 200) {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.coupons ?? response.data?.data ?? [];
        const sorted = sortByRecentlyAdded(list);
        setCoupons(sorted);
        setTotalRecords(response.data?.totalRecords || list.length);
        fetchExpiredCounts(sorted);
      } else {
        setCoupons([]);
      }
    } catch {
      setCoupons([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableCouponsForUser = async (userId) => {
    setIsLoading(true);
    try {
      const response = await getApi(`${GET_AVAILABLE_COUPONS}/${userId}`);
      if (response && response.status === 200) {
        const list = Array.isArray(response.data) ? response.data : [];
        const sorted = sortByRecentlyAdded(list);
        setCoupons(sorted);
        setTotalRecords(list.length);
        fetchExpiredCounts(sorted);
      } else {
        setCoupons([]);
        setTotalRecords(0);
        errorToast(response?.message || "Failed to fetch available coupons for user");
      }
    } catch {
      setCoupons([]);
      setTotalRecords(0);
      errorToast("Failed to load available coupons for user");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilterEligibleUsers = async () => {
    setFilterUsersLoading(true);
    try {
      const response = await getApi(GET_ELIGIBLE_USERS);
      if (response && response.status === 200) {
        setFilterEligibleUsers(Array.isArray(response.data) ? response.data : []);
      } else {
        setFilterEligibleUsers([]);
      }
    } catch {
      setFilterEligibleUsers([]);
    } finally {
      setFilterUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterEligibleUsers();
  }, []);

  useEffect(() => {
    if (filterUserId) {
      fetchAvailableCouponsForUser(filterUserId);
    } else {
      fetchCoupons();
    }
  }, [filterStatus, filterActive, filterUserId]);

  // Re-fetch whichever view (filtered-by-user or the general list) is currently active
  const refreshCoupons = () => {
    if (filterUserId) {
      fetchAvailableCouponsForUser(filterUserId);
    } else {
      fetchCoupons();
    }
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    createForm.resetFields();
  };

  const handleCreateCoupon = async (values) => {
    setCreateLoading(true);
    try {
      const payload = {
        code: values.code.toUpperCase(),
        discountType: values.discountType,
        discountValue: values.discountValue,
        maxUsageCount: values.maxUsageCount,
      };
      const response = await postApi(CREATE_COUPON, payload);
      if (response && (response.status === 200 || response.status === 201)) {
        successToast(response.message || "Coupon created successfully!");
        closeCreateModal();
        refreshCoupons();
      } else {
        errorToast(response?.message || "Failed to create coupon");
      }
    } catch {
      errorToast("An error occurred while creating the coupon.");
    } finally {
      setCreateLoading(false);
    }
  };

  const columns = [
    {
      title: "SL No",
      key: "slNo",
      width: 70,
      render: (_, __, index) => (pageIndex - 1) * pageSize + (index + 1),
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code) => (
        <Text strong className="coupon-code" style={{ color: "#6c47ff" }}>
          {code}
        </Text>
      ),
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => (
        <StatChip tone="violet">
          {record.discountType === "PERCENTAGE"
            ? `${Number(record.discountValue)}%`
            : `₹${Number(record.discountValue)}`}
        </StatChip>
      ),
    },
    {
      title: "Total Coupons",
      dataIndex: "maxUsageCount",
      key: "maxUsageCount",
      render: (val) => <StatChip tone="slate">{val ?? 0}</StatChip>,
    },
    {
      title: "Unassigned",
      key: "unassigned",
      render: (_, record) => (
        <StatChip tone="amber">
          {Math.max((record.maxUsageCount ?? 0) - (record.assignedCount ?? 0), 0)}
        </StatChip>
      ),
    },
    {
      title: "Assigned",
      dataIndex: "assignedCount",
      key: "assignedCount",
      render: (val) => <StatChip tone="blue">{val ?? 0}</StatChip>,
    },
    {
      title: "Redeemed",
      dataIndex: "currentUsageCount",
      key: "currentUsageCount",
      render: (val) => <StatChip tone="emerald">{val ?? 0}</StatChip>,
    },
    {
      title: "Unredeemed",
      key: "unredeemed",
      render: (_, record) => (
        <StatChip tone="rose">
          {Math.max(
            (record.assignedCount ?? 0) -
              (record.currentUsageCount ?? 0) -
              (expiredCounts[record.id] ?? 0),
            0
          )}
        </StatChip>
      ),
    },
    {
      title: "Expired",
      key: "expiredCount",
      render: (_, record) =>
        expiredCountsLoading && !(record.id in expiredCounts) ? (
          <Spin size="small" />
        ) : (
          <StatChip tone="rose">{expiredCounts[record.id] ?? 0}</StatChip>
        ),
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    //   render: (status) => (
    //     <Tag color={STATUS_COLORS[status?.toLowerCase()] || "default"}>
    //       {status || "—"}
    //     </Tag>
    //   ),
    // },
  ];

  return (
    <div className="coupons-page">
      {isLoading && <Loader />}

      <div className="coupons-header">
        <Space style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Title level={3} style={{ margin: 0 }}>
            Coupons Management
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Create Coupon
          </Button>
        </Space>
      </div>

      <div className="coupons-content">
        <div className="coupons-filters">
          <Select
            placeholder="Filter by status"
            allowClear
            disabled={!!filterUserId}
            style={{ width: 180 }}
            value={filterStatus}
            onChange={(val) => setFilterStatus(val)}
          >
            <Option value="ACTIVE">Active</Option>
            <Option value="ASSIGNED">Assigned</Option>
          </Select>

          {/* <Space align="center">
            <Text>Active only</Text>
            <Switch
              checked={filterActive === true}
              disabled={!!filterUserId}
              onChange={(checked) => setFilterActive(checked ? true : undefined)}
            />
          </Space> */}

          <Select
            showSearch
            allowClear
            placeholder="Filter by user"
            style={{ width: 260 }}
            value={filterUserId}
            loading={filterUsersLoading}
            onChange={(val) => setFilterUserId(val)}
            optionFilterProp="label"
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
            options={filterEligibleUsers.map((user) => ({
              value: String(user.id),
              label: `${user.firstName} ${user.lastName} ${user.email}`,
            }))}
          />
        </div>

        {coupons.length > 0 ? (
          <div className="table-data">
            <Table
              columns={columns}
              dataSource={coupons}
              rowKey="id"
              onRow={(record) => ({
                onClick: () => navigate(`/coupons/${record.id}`),
                style: { cursor: "pointer" },
              })}
              pagination={{
                current: pageIndex,
                pageSize,
                total: totalRecords,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                onChange: (page, size) => {
                  setPageIndex(page);
                  setPageSize(size);
                },
              }}
              scroll={{ x: "max-content" }}
            />
          </div>
        ) : (
          !isLoading && (
            <Empty
              className="coupons-empty"
              description={
                <div>
                  <Title level={4}>No Coupons Found</Title>
                  <Text>Click "Create Coupon" to add your first coupon.</Text>
                </div>
              }
              imageStyle={{ height: 60 }}
            />
          )
        )}
      </div>

      {/* Create Coupon Modal */}
      <Modal
        title="Create New Coupon"
        open={createModalOpen}
        onCancel={closeCreateModal}
        footer={null}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateCoupon}>
          <Form.Item
            name="code"
            label="Coupon Code"
            rules={[{ required: true, message: "Please enter a coupon code" }]}
          >
            <Input placeholder="e.g. YOGA20" />
          </Form.Item>

          <Form.Item
            name="discountType"
            label="Discount Type"
            rules={[{ required: true, message: "Please select a discount type" }]}
            initialValue="FIXED"
          >
            <Select placeholder="Select discount type">
              <Option value="FIXED">Fixed Amount (₹)</Option>
              <Option value="PERCENTAGE">Percentage (%)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.discountType !== curr.discountType}
          >
            {({ getFieldValue }) => {
              const discountType = getFieldValue("discountType");
              const isPercentage = discountType === "PERCENTAGE";
              return (
                <Form.Item
                  name="discountValue"
                  label={isPercentage ? "Discount Value (%)" : "Discount Value (₹)"}
                  rules={[{ required: true, message: "Please enter a discount value" }]}
                >
                  <InputNumber
                    min={0}
                    max={isPercentage ? 100 : undefined}
                    placeholder={isPercentage ? "e.g. 10" : "e.g. 100"}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            name="maxUsageCount"
            label="Number of Coupons"
            rules={[{ required: true, message: "Please enter the maximum usage count" }]}
          >
            <InputNumber min={0} placeholder="e.g. 100" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={closeCreateModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={createLoading}>
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Coupons;
